import React, { useCallback, useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import ReportFiltersListCard from './ReportFiltersListCard';
import { initialState, reducer } from '../../../reducers/reportFilters/containerReducer';
import { useToast } from 'hooks/toasts/useToast';
import { useValidation } from '@totalsoft/pure-validations-react';
import { reportFilterValidator } from 'features/reports/validators';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { DICTIONARY_EDITOR_TYPES_QUERY } from '../queries';
import { REMOVE_REPORT_FILTER_INPUT, ADD_UPDATE_REPORT_FILTER } from '../mutations';
import { REPORT_FILTER_UPDATED, REPORT_FILTER_REMOVED } from '../subscriptions';
import useLanguageTracker from 'hooks/useLanguageTracker';

const ReportFiltersContainer = ({ data, reportId }) => {
  const addToast = useToast();
  const [t] = useTranslation();

  const [localState, dispatch] = useReducer(reducer, initialState);
  const [reportFiltersValidation, validateReportFilters] = useValidation(reportFilterValidator);

  // #region Queries

  const { data: dictionaryEditorTypesData, refetch: refetchDictionaryEditorTypes } = useQuery(DICTIONARY_EDITOR_TYPES_QUERY);

  // #endregion

  // #region Mutations

  const [addOrUpdateReportFilter] = useMutation(ADD_UPDATE_REPORT_FILTER);

  const [removeReportFilter] = useMutation(REMOVE_REPORT_FILTER_INPUT);

  // #endregion

  // #region Subscriptions

  useSubscription(REPORT_FILTER_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('Reports.ReportFilterUpdated'), 'success');
      client.cache.evict({
        id: `ReportItem:{"reportId":${data.reportFilterUpdated.reportFilter.reportId}}`
      });
    }
  });

  useSubscription(REPORT_FILTER_REMOVED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('Reports.ReportFilterRemoved'), 'success');
      client.cache.evict({
        id: `ReportFilterItem:{"reportFilterId":${data.reportFilterRemoved.filterId}}`
      });
    }
  });

  // #endregion

  // #region Side effects

  const onLanguageChange = useCallback(() => {
    refetchDictionaryEditorTypes();
  }, [refetchDictionaryEditorTypes]);

  useLanguageTracker({
    onChange: onLanguageChange
  });

  useEffect(() => {
    if (data) dispatch({ type: 'OnInitState', data });
  }, [data]);

  useEffect(() => {
    validateReportFilters(localState.values);
  }, [localState.values, validateReportFilters]);

  // #endregion

  // #region Handlers

  const handleAdd = () => {
    dispatch({
      type: 'OnReportFilterAdded',
      reportId
    });
  };

  const handleExpandedChanged = (isExpanded, index) => {
    dispatch({ type: 'OnReportFilterExpanded', index, isExpanded });
  };

  const handleDelete = (reportFilter, index) => () => {
    if (window.confirm(t('Reports.ReportFilters.DeleteReportFilter'))) {
      if (reportFilter.reportFilterId) {
        const input = { reportFilterId: reportFilter.reportFilterId, reportId: reportFilter.reportId };
        removeReportFilter({ variables: { input } });
      }
      dispatch({ type: 'OnReportFilterDeleted', index });
    }
  };

  const handlePropertyChanged = (propertyName, value, index) => {
    dispatch({ type: 'OnReportFilterPropertyChanged', propertyName, value, index });
  };

  const handleSave = index => () => {
    if (!validateReportFilters(localState.values)) return;
    // eslint-disable-next-line no-unused-vars
    const { changed, isExpanded, ...reportFilter } = localState.values[index];
    const input = { ...reportFilter };

    dispatch({ type: 'OnReportFilterSaved', index });
    return addOrUpdateReportFilter({ variables: { input } });
  };

  // #endregion

  return (
    <ReportFiltersListCard
      reportFilters={localState.values}
      onAdd={handleAdd}
      onPropertyChanged={handlePropertyChanged}
      onExpandedChanged={handleExpandedChanged}
      onSave={handleSave}
      onDelete={handleDelete}
      dictionaryEditorTypes={dictionaryEditorTypesData ? dictionaryEditorTypesData.dictionaryEditorTypes : []}
      validation={reportFiltersValidation}
    />
  );
};

ReportFiltersContainer.propTypes = {
  data: PropTypes.array,
  reportId: PropTypes.number
};

export default ReportFiltersContainer;
